/**
 * The forge on the storefront: the builder's rows from a published version and the drop table
 * the API takes, what the name field says under a name the rules refuse, the message for each way
 * a write can fail, and the mutations. The builder's arithmetic and what its submit still needs
 * are `forgeBuilder.ts` in api-contract, which the theme preview reads too, so it prices and
 * blocks as the store does.
 */
import {
  type BoxDetail,
  type Currency,
  FORGE_MAX_ITEMS,
  FORGE_MIN_ITEMS,
  FORGE_NAME_MAX,
  type ForgeBoxStatusResponse,
  type ForgeBoxStatusValue,
  type ForgeBoxWriteResponse,
  type ForgeClaimResponse,
  type ForgeRow,
  forgeNameIssue,
  formatAmountWithUnit,
  percentToWeight,
  type StorefrontTerminology,
  weightToPercent,
} from "@loot-os/api-contract";
import { isLootApiErrorLike } from "@loot-os/react";
import { useMutation } from "@tanstack/react-query";

import { invalidateAfterForgeChange, invalidateAfterForgeClaim, lootFetch, qk } from "./queries.js";
import { queryClient } from "./queryClient.js";

/** The drop table as the API takes it. Only called once the weights total 1,000,000. */
export function toDropTableInput(rows: readonly ForgeRow[]) {
  return rows.map((row) => ({ catalogItemId: row.id, weight: percentToWeight(row.percent) }));
}

/** The builder's rows from a published version, for the edit page. */
export function rowsFromDetail(detail: BoxDetail): ForgeRow[] {
  return detail.dropTable.map((entry) => ({
    id: entry.itemId,
    name: entry.name,
    imageUrl: entry.imageUrl,
    valueCents: entry.value,
    percent: weightToPercent(entry.weight),
  }));
}

/** What the name field says under a name the shopper has typed, or null. */
export function forgeNameHint(name: string): string | null {
  switch (forgeNameIssue(name)) {
    case "too_long":
      return `At most ${FORGE_NAME_MAX} characters.`;
    case "special_characters":
      return "Letters, numbers and spaces only: no punctuation or symbols.";
    default:
      return null;
  }
}

function detailString(details: Record<string, unknown> | undefined, key: string): string | null {
  const value = details?.[key];
  return typeof value === "string" ? value : null;
}

function detailNumber(details: Record<string, unknown> | undefined, key: string): number | null {
  const value = details?.[key];
  return typeof value === "number" ? value : null;
}

export interface ForgeErrorContext {
  terms: StorefrontTerminology;
  currency: Currency;
  /** The name of a picked item by id, so a refusal can say which one. */
  itemName?: (id: string) => string | undefined;
}

/** Why a drop table was refused, per `details.reason`. */
function dropTableText(details: Record<string, unknown> | undefined, ctx: ForgeErrorContext) {
  const { item, box } = ctx.terms;
  const itemId = detailString(details, "itemId");
  const named = (itemId && ctx.itemName?.(itemId)) || `One ${item.singular}`;
  switch (detailString(details, "reason")) {
    case "too_few_items":
      return `Pick at least ${FORGE_MIN_ITEMS} ${item.plural}.`;
    case "too_many_items":
      return `Pick at most ${FORGE_MAX_ITEMS} ${item.plural}.`;
    case "duplicate_item":
      return `${named} is in the ${box.singular} twice.`;
    case "zero_weight":
    case "invalid_weight":
      return `${named} needs a pull rate above 0%.`;
    case "weights_sum":
      return "Pull rates must total exactly 100%.";
    case "item_not_found":
      return `${named} is no longer available. Remove it and try again.`;
    case "item_not_fulfillable":
      return `${named} can no longer be shipped. Remove it and try again.`;
    case "item_outside_categories":
      return `${named} isn't in a category this store offers. Remove it and try again.`;
    case "item_below_minimum": {
      const min = detailNumber(details, "minimumCents");
      return min === null
        ? `${named} is worth too little for the ${ctx.terms.forge.singular}. Remove it.`
        : `${named} is worth less than ${formatAmountWithUnit(min, ctx.currency)}. Remove it.`;
    }
    case "item_above_maximum": {
      const max = detailNumber(details, "maximumCents");
      return max === null
        ? `${named} is worth too much for the ${ctx.terms.forge.singular}. Remove it.`
        : `${named} is worth more than ${formatAmountWithUnit(max, ctx.currency)}. Remove it.`;
    }
    default:
      return `This ${box.singular} cannot be saved as it is. Check the ${item.plural} and pull rates.`;
  }
}

/** What to tell the shopper when a forge write failed. */
export function forgeErrorMessage(err: unknown, ctx: ForgeErrorContext): string {
  const { box } = ctx.terms;
  if (!isLootApiErrorLike(err)) {
    return err instanceof Error && err.message ? err.message : "Something went wrong. Try again.";
  }
  // The published client's ErrorCode predates the forge, so the code is compared as a string.
  const code: string = err.code;
  const details = err.details;
  const reason = detailString(details, "reason");
  switch (code) {
    case "forge_drop_table_invalid":
      return dropTableText(details, ctx);
    case "forge_cut_invalid": {
      if (reason === "commission_disabled") {
        return "This store pays no commission, so your cut must be 0%.";
      }
      const max = detailNumber(details, "maxCreatorCutPercent");
      if (reason === "above_maximum" && max !== null) return `Your cut can be at most ${max}%.`;
      return "Pick a cut between 0% and the store's maximum.";
    }
    case "forge_name_rejected":
      switch (reason) {
        case "empty":
          return "Enter a name.";
        case "too_long":
          return `Names are at most ${FORGE_NAME_MAX} characters.`;
        case "special_characters":
          return "Names can use letters, numbers and spaces only.";
        case "profanity":
          return "That name is not allowed. Try another.";
        default:
          return "That name is not allowed.";
      }
    case "forge_image_invalid":
      return reason === "not_offered"
        ? "That image is no longer offered. Pick another."
        : `Pick an image for your ${box.singular}.`;
    case "forge_daily_limit":
      return `You have built as many ${box.plural} as you can today. Try again tomorrow.`;
    case "forge_disabled":
      return `The ${ctx.terms.forge.singular} is turned off on this store.`;
    case "forge_nothing_to_claim":
      return "There are no earnings to claim yet.";
    case "username_required":
      return "Choose a username first: other players see it as the creator.";
    case "box_version_changed":
      return `This ${box.singular} changed since you loaded it. The latest pull rates are loaded; review them and save again.`;
    case "platform_switch_disabled":
      return `The ${ctx.terms.forge.singular} is paused right now. Try again later.`;
    case "rate_limited":
      return "Too many changes at once. Wait a minute and try again.";
    case "account_locked":
      return "Your account is locked.";
    case "not_found":
      return `That ${box.singular} is gone.`;
    default:
      return err.message || "Something went wrong. Try again.";
  }
}

// ---- mutations ---------------------------------------------------------------------------------

export interface CreateForgeBoxInput {
  name: string;
  imageUrl: string;
  creatorCutPercent: number;
  rows: readonly ForgeRow[];
}

/** The box page shows the new version at once: the write answered with it. */
function applyForgeWrite(res: ForgeBoxWriteResponse): void {
  queryClient.setQueryData(qk.box(res.box.id), res.detail);
  invalidateAfterForgeChange();
}

/** POST /v1/forge/boxes. */
export function useCreateForgeBox() {
  return useMutation({
    mutationFn: (input: CreateForgeBoxInput) =>
      lootFetch<ForgeBoxWriteResponse>("/forge/boxes", {
        method: "POST",
        body: JSON.stringify({
          name: input.name.trim(),
          imageUrl: input.imageUrl,
          creatorCutPercent: input.creatorCutPercent,
          dropTable: toDropTableInput(input.rows),
        }),
      }),
    onSuccess: applyForgeWrite,
  });
}

export interface UpdateForgeDropTableInput {
  creatorCutPercent: number;
  rows: readonly ForgeRow[];
  expectedVersion: number;
}

/** PUT /v1/forge/boxes/{boxId}/drop-table, pinned to the version the builder loaded. */
export function useUpdateForgeDropTable(boxId: string) {
  return useMutation({
    mutationFn: (input: UpdateForgeDropTableInput) =>
      lootFetch<ForgeBoxWriteResponse>(`/forge/boxes/${encodeURIComponent(boxId)}/drop-table`, {
        method: "PUT",
        body: JSON.stringify({
          creatorCutPercent: input.creatorCutPercent,
          dropTable: toDropTableInput(input.rows),
          expectedVersion: input.expectedVersion,
        }),
      }),
    onSuccess: applyForgeWrite,
  });
}

/** PATCH /v1/forge/boxes/{boxId}: archive, unarchive or delete for good. */
export function useSetForgeBoxStatus() {
  return useMutation({
    mutationFn: (input: { boxId: string; status: ForgeBoxStatusValue }) =>
      lootFetch<ForgeBoxStatusResponse>(`/forge/boxes/${encodeURIComponent(input.boxId)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: input.status }),
      }),
    onSuccess: (res) => invalidateAfterForgeChange(res.box.id),
  });
}

/** POST /v1/me/forge/earnings/claim. One key per click: a retried request replays the claim. */
export function useClaimForgeEarnings() {
  return useMutation({
    mutationFn: () =>
      lootFetch<ForgeClaimResponse>("/me/forge/earnings/claim", {
        method: "POST",
        headers: { "idempotency-key": crypto.randomUUID() },
      }),
    onSuccess: invalidateAfterForgeClaim,
  });
}
